import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Flex, Loader, Stack, TabsList, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { LogoWithIcon } from 'src/UI/app-container/header/Header';
import { Tab, TabPanel, Tabs } from 'src/components';
import SignupTabContent from 'src/auth/login/SignupTabContent';
import LoginTabContent from 'src/auth/login/LoginTabContent';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { AuthCard } from 'src/auth/components/AuthCard';
import { getInviteData, clearInviteData } from 'src/auth/utils/inviteStorage';
import { Configuration, FrontendApi, LoginFlow } from '@ory/client';
import { isOryApiError } from 'src/auth/utils/ory-helpers';
import Config from 'src/common/Config';
import { Logger } from 'src/utils/Logger';
import { useSearchParams } from 'react-router';
import { useIsMobile } from 'src/common/hooks/useIsMobile';

const AUTH_METHOD_LINK_RECOVERY = 'link_recovery';
const MAX_FLOW_CREATION_ATTEMPTS = 3;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const ory = new FrontendApi(
  new Configuration({
    basePath: Config.getAuthBasePath(),
    baseOptions: {
      withCredentials: true, // important for cookies
    },
  }),
);

const LOGIN_TABS = {
  LOGIN: 'LogIn',
  SIGNUP: 'SignUp',
};

export const LoginPage = () => {
  const [flow, setFlow] = useState<LoginFlow>();
  const [flowError, setFlowError] = useState<string | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get('flow');
  const { isMobile } = useIsMobile();
  const [inviteData] = useState(() => getInviteData());
  const inviteEmail = inviteData?.email ?? null;
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(() => !!inviteData);

  const advanceFlowToPassword = async (loginFlow: LoginFlow, email: string): Promise<LoginFlow> => {
    const hasPasswordNode = loginFlow.ui.nodes.some(
      node =>
        node.group === 'default' &&
        'name' in node.attributes &&
        node.attributes.name === 'password',
    );
    if (hasPasswordNode) return loginFlow;

    const csrfToken =
      (
        loginFlow.ui.nodes.find(
          node => 'name' in node.attributes && node.attributes.name === 'csrf_token',
        )?.attributes as { value?: string }
      )?.value || '';

    try {
      await ory.updateLoginFlow({
        flow: loginFlow.id,
        updateLoginFlowBody: {
          method: 'password',
          identifier: email,
          csrf_token: csrfToken,
        } as Parameters<typeof ory.updateLoginFlow>[0]['updateLoginFlowBody'],
      });
      return loginFlow;
    } catch (e: unknown) {
      if (
        e &&
        typeof e === 'object' &&
        'response' in e &&
        e.response &&
        typeof e.response === 'object' &&
        'data' in e.response &&
        e.response.data
      ) {
        const responseData = e.response.data as LoginFlow;
        if ('ui' in responseData) {
          return responseData;
        }
      }
      return loginFlow;
    }
  };

  useEffect(() => {
    const initializeFlow = async () => {
      if (flowId) {
        try {
          const { data } = await ory.getLoginFlow({ id: flowId });
          const finalFlow = inviteEmail ? await advanceFlowToPassword(data, inviteEmail) : data;
          setFlow(finalFlow);
          return;
        } catch (e) {
          if (isOryApiError(e) && e.response?.status === 410) {
            // Flow expired - clear stale URL param and fall through to create new flow
            window.history.replaceState({}, '', window.location.pathname);
          } else {
            setFlowError((e as Error).message);
            Logger.error('Could not get login flow.', e);
            return;
          }
        }
      }

      try {
        const { data: session } = await ory.toSession();
        const authMethods = session.authentication_methods ?? [];
        const mostRecentMethod = authMethods[authMethods.length - 1]?.method;

        if (mostRecentMethod === AUTH_METHOD_LINK_RECOVERY) {
          // Invite flow session - force logout so user must authenticate with proper credentials
          try {
            const { data: logoutFlow } = await ory.createBrowserLogoutFlow();
            await ory.updateLogoutFlow({ token: logoutFlow.logout_token });
          } catch {
            // Logout failed - proceed anyway
          }
        } else {
          // User has a proper session, redirect to app
          window.location.replace('/');
          return;
        }
      } catch {
        // No session - proceed to create login flow
      }

      let lastError: unknown;
      for (let attempt = 1; attempt <= MAX_FLOW_CREATION_ATTEMPTS; attempt++) {
        try {
          const { data } = await ory.createBrowserLoginFlow();
          const finalFlow = inviteEmail ? await advanceFlowToPassword(data, inviteEmail) : data;
          setFlow(finalFlow);
          return;
        } catch (e) {
          lastError = e;
          if (attempt < MAX_FLOW_CREATION_ATTEMPTS) {
            await delay(500 * attempt);
          }
        }
      }
      setFlowError((lastError as Error).message);
      Logger.error('Could not initialize login flow after multiple attempts.', lastError);
    };

    initializeFlow();
  }, [flowId, inviteEmail]);

  const resetFlow = async () => {
    try {
      const response = await ory.createBrowserLoginFlow();
      setFlow(response.data);
    } catch (e) {
      setFlowError('Could not reset login flow.');
      Logger.error('Could not reset login flow.', e);
    }
  };

  const handleBannerClose = () => {
    setShowWelcomeBanner(false);
    clearInviteData();
  };

  const renderLoginContent = () => {
    if (flow) {
      return (
        <LoginTabContent
          resetFlow={resetFlow}
          flow={flow}
          setError={setFlowError}
          setFlow={setFlow}
          initialEmail={inviteEmail}
        />
      );
    }

    if (flowError) {
      return (
        <Stack mt="xl" gap="xl">
          <Text c="red">
            We could not connect to the sign in service. This is usually temporary — please try
            refreshing the page.
          </Text>
          <Button onClick={() => window.location.reload()}>Refresh page</Button>
        </Stack>
      );
    }

    return (
      <Flex w={'100%'} justify={'center'} mt={'xl'}>
        <Loader />
      </Flex>
    );
  };

  return (
    <AuthPageLayout>
      <AuthCard w={isMobile ? '100%' : 480}>
        <Box mb="xxl">
          <LogoWithIcon textColor="gray.10" />
        </Box>

        {showWelcomeBanner && (
          <Alert
            color="green"
            icon={<IconCheck size={16} />}
            mb="md"
            withCloseButton
            onClose={handleBannerClose}
          >
            Account created successfully! Please sign in with your new credentials.
          </Alert>
        )}

        <Tabs variant="secondary" defaultValue="LogIn">
          <TabsList>
            <Tab value={LOGIN_TABS.LOGIN}>Log in</Tab>
            <Tab value={LOGIN_TABS.SIGNUP}>Sign up</Tab>
          </TabsList>

          <TabPanel value={LOGIN_TABS.LOGIN}>{renderLoginContent()}</TabPanel>
          <TabPanel value={LOGIN_TABS.SIGNUP}>
            <SignupTabContent />
          </TabPanel>
        </Tabs>
      </AuthCard>
    </AuthPageLayout>
  );
};
